import styled from 'styled-components';
import { colors } from '../GlobalStyles';

const TeamContainer = styled.section`
  background: ${colors.white};
  padding: 5rem 2rem;
  text-align: center;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    min-height: 500px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${colors.textDark};
  margin-bottom: 3rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const TeamMemberImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1.5rem;
  border: 4px solid ${colors.lightBeige};

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const MemberName = styled.h3`
  font-size: 1.5rem;
  color: ${colors.textDark};
  margin-bottom: 0.5rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const MemberRole = styled.p`
  color: ${colors.accentOrange};
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const MemberDescription = styled.p`
  color: ${colors.textGray};
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const LinkedInButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #0077b5;
  color: ${colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #005885;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }
`;

interface EmployeeProp {
  name: string;
  experienceLevel: string;
  mainFocus: string;
  techStack: string;
  linkedinUrl: string;
  image: string;
}

export function Employee({
  name,
  experienceLevel,
  mainFocus,
  techStack,
  linkedinUrl,
  image
}: EmployeeProp) {
  return (
    <TeamCard>
      <TeamMemberImage src={image} alt={`${name}`} />
      <MemberName>{name}</MemberName>
      <MemberRole>Senior Consultant</MemberRole>
      <MemberDescription>
        <strong>Experience:</strong> {experienceLevel}
        <br />
        <strong>Focus:</strong> {mainFocus}
        <br />
        <strong>Tech Stack:</strong> {techStack}
      </MemberDescription>
      <LinkedInButton
        href={`https://www.linkedin.com/in/${linkedinUrl}`}
        target="_blank"
        rel="noreferrer"
      >
        <span>🔗</span> LinkedIn
      </LinkedInButton>
    </TeamCard>
  );
}

interface EmployeesProp {
  employees: EmployeeProp[];
}

export function Employees({ employees }: EmployeesProp) {
  return (
    <TeamContainer id="team">
      <SectionTitle>Meet our team!</SectionTitle>
      <TeamGrid>
        {employees.map((employee, key) => (
          <Employee key={key} {...employee} />
        ))}
      </TeamGrid>
    </TeamContainer>
  );
}
